import { Avatar, Box, Button, Drawer, DrawerBody, DrawerContent, DrawerHeader, DrawerOverlay, Flex, IconButton } from '@chakra-ui/react';
import { createContext, MutableRefObject, PropsWithChildren, useContext, useEffect, useRef, useState } from 'react';
import { FaBars } from 'react-icons/fa6';
import { useLocation } from 'react-router';

import { ProfileContext } from '@app/contexts/profile';
import { useApi } from '@app/hooks';

import { ErrorBoundary } from '../error-boundary/error-boundary';
import Logo from './icons/logo';
import { Menu } from './navigation/menu';
import { Txt } from './text';

type LayoutProps = PropsWithChildren & {
  hideHeaderAndMenu: boolean
  loading: boolean
}

type LayoutState = {
  containerRef: MutableRefObject<HTMLDivElement>
}

export const LayoutContext = createContext<LayoutState>({
  containerRef: null
});

export const Layout = ({ children, hideHeaderAndMenu, loading }: LayoutProps) => {
  const { profile } = useContext(ProfileContext);
  const api = useApi();
  const [mobileOpen, setMobileOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const onCheckOut = () => {
    console.log('onCheckOut');
    api.auth.singOut();
  };

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };
  const location = useLocation();
  useEffect(() => {
    setMobileOpen(false);
  }, [location, setMobileOpen]);


  return (
    <Box 
      display='grid' 
      gridTemplateColumns={hideHeaderAndMenu ? '1fr' : '260px 1fr'}
      gridTemplateRows={hideHeaderAndMenu ? '1fr' : '60px 1fr'}
      w='100%' minW='100vw' maxW='100vw'
    >
      {!hideHeaderAndMenu && <Box w='100%' h='60px' bg='brand.500' gridColumn='span 2' 
        boxShadow='0px 3px 3px -2px rgba(0,0,0,0.2), 0px 3px 4px 0px rgba(0,0,0,0.14), 0px 1px 8px 0px rgba(0,0,0,0.12)' 
        display='flex'
        justifyContent='space-between'
        alignItems='center'
        pl={2}
        pr={4}
      >            
        <Box display={{ base: 'block', md: 'none' }} >
          <IconButton 
            variant='ghost' 
            colorScheme='light'
            borderRadius='50%' 
            icon={<FaBars size='32px' color='white' />} 
            aria-label="burger" 
            onClick={handleDrawerToggle} 
          />
        </Box>
        <Flex alignItems='center' display={{ base: 'none', md: 'flex' }} >
          <Logo variant="horizontal" />
          <Txt ml={-3} fontFamily='monospace' mt={1} color='brand.100' >.web [APIv3]</Txt>
        </Flex>
        <Box display='flex' alignItems='center' gap={2}>
          <Button variant='link' colorScheme='light' onClick={onCheckOut}>Выйти</Button>
          <Avatar size="sm" name={profile.first_name || profile.username} 
            color='brand.300' bgColor='gray.200' fontSize='2xl'
          />
        </Box>
      </Box>}
      {!hideHeaderAndMenu && <Box w='260px' h='calc(100vh - 60px)' 
        overflowY='scroll'
        display={{ base: 'none', md: 'block' }}
        backgroundColor='dark.900'
      >
        <Menu />
      </Box>}
      <Box w='100%' h='calc(100vh - 60px)' 
        p={2} overflow='auto'
        gridColumn={{base: 'span 2', md: 'unset' }}
        position='relative'
        ref={containerRef}
      >
        <LayoutContext.Provider value={{ containerRef }}>
          <ErrorBoundary>{children}</ErrorBoundary>
        </LayoutContext.Provider>
      </Box>
      <Drawer
        isOpen={mobileOpen}
        placement='left'
        onClose={handleDrawerToggle}
      >
        <DrawerOverlay />
        <DrawerContent>
          <DrawerHeader bgColor='brand.500' minH='60px' p={0} >
            <Logo variant="horizontal" />
          </DrawerHeader>
          <DrawerBody p={0}>
            <Menu />
          </DrawerBody>
        </DrawerContent>
      </Drawer>
    </Box>
  );
};

export const useLayout = () => useContext(LayoutContext);